import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  footer?: React.ReactNode
}

export function Drawer({ isOpen, onClose, title, children, footer }: DrawerProps) {
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>
    if (isOpen) {
      setShouldRender(true)
      // A small delay to ensure the initial transition class is applied
      timeoutId = setTimeout(() => {
        setIsAnimating(true)
      }, 50)
    } else {
      setIsAnimating(false)
      // Wait for transition duration (300ms) before unmounting
      timeoutId = setTimeout(() => {
        setShouldRender(false)
      }, 300)
    }

    return () => clearTimeout(timeoutId)
  }, [isOpen])

  if (!shouldRender) return null

  const drawerContent = (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-indigo-950/40 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Drawer Body Container */}
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div
          className={`w-screen max-w-md transform bg-surface border-l border-border shadow-2xl transition-all duration-300 flex flex-col h-full ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-6 py-5">
            <h3 className="text-lg font-bold text-text-primary tracking-tight leading-none">{title}</h3>
            <button
              onClick={onClose}
              className="rounded-btn p-1.5 text-text-secondary hover:bg-bg hover:text-text-primary transition-colors"
              aria-label="Close panel"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 text-sm text-text-secondary">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="flex items-center justify-end gap-3 border-t border-border px-6 py-4 bg-bg/50">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return <>{ReactDOM.createPortal(drawerContent, document.body)}</>
}
